import { useEffect, useRef } from 'react'
import { Children, isValidElement, ReactNode, useId, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { smoothScroll } from '@/utils/dom'
import { cx } from '@/utils/strings'

type TabGroupProps = {
  children: ReactNode
}

export default function TabGroup({ children }: TabGroupProps) {
  const [selectedTab, setSelectedTab] = useState(0)
  const uid = useId().slice(1, -1)
  const { url } = usePage()

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const hash = url.split('#')[1]
  useEffect(() => {
    if (hash) {
      const index = tabRefs.current.findIndex((ref) => ref?.id === hash)
      if (index !== -1 && tabRefs.current[index]) {
        tabRefs.current[index]?.focus()
        setTimeout(() => tabRefs.current[index]?.click(), 1)
      }
    }
  }, [hash])

  return (
    children && (
      <div className="mb-16">
        {/* The tablist must directly contain elements with role="tab" */}
        <div
          className="flex flex-wrap -mb-0.5 list-none rounded-md lg:rounded-none"
          role="tablist"
          aria-orientation="horizontal"
        >
          {Children.map(
            children,
            (item, index) =>
              isValidElement(item) && (
                <button
                  ref={(el) => (tabRefs.current[index] = el)}
                  type="button"
                  data-id={`${uid}_tab_${index}`}
                  id={item.props.tabId}
                  role="tab"
                  aria-selected={selectedTab === index ? 'true' : 'false'}
                  aria-controls={`panel-${item.props.tabId}`}
                  tabIndex={selectedTab === index ? 0 : -1}
                  className={cx(
                    'tab-header transition mt-0 mb-0 rounded-t-md mr-[3px] px-6 py-3 leading-tight text-base border-2 border-secondary-200 border-b-secondary-200 border-b-transparent text-secondary-800 dark:text-white dark:border-none',
                    selectedTab === index
                      ? 'font-medium bg-secondary-50 dark:bg-secondary-800'
                      : 'font-normal bg-secondary-200 hover:bg-secondary-100 dark:bg-secondary-600 dark:border-none'
                  )}
                  onClick={(e) => {
                    setSelectedTab(index)
                    smoothScroll(e, 20)
                  }}
                  onKeyDown={(e) => {
                    let move = 0
                    if (e.code === 'ArrowRight') move = 1
                    else if (e.code === 'ArrowLeft') move = -1
                    if (move !== 0) {
                      let newIndex = selectedTab + move
                      const length = Children.count(children)
                      if (newIndex < 0) newIndex = length - 1
                      else if (newIndex >= length) newIndex = 0
                      const elem = document.querySelector(
                        `[data-id='${uid}_tab_${newIndex}']`
                      ) as HTMLInputElement | null
                      if (elem) {
                        elem.click()
                        elem.focus()
                        e.preventDefault()
                      }
                    }
                  }}
                >
                  {item.props.buttonContent}
                </button>
              )
          )}
        </div>
        <div id="tab-parent">
          {Children.map(
            children,
            (item, index) =>
              isValidElement(item) && (
                <div
                  role="tabpanel"
                  key={`${uid}_tabpanel_${index}`}
                  id={`panel-${item.props.tabId || `${uid}-${index}`}`}
                  aria-labelledby={`tab-${item.props.tabId || `${uid}-${index}`}`}
                  aria-hidden={selectedTab !== index}
                  className={cx(
                    'tab-panel p-6 bg-secondary-50 border-2 border-secondary-200 rounded-sm dark:bg-secondary-800 dark:border-none',
                    selectedTab === index ? 'block' : 'hidden'
                  )}
                >
                  {item.props.children}
                </div>
              )
          )}
        </div>
      </div>
    )
  )
}

type TabItemProps = {
  children: ReactNode
  buttonContent: ReactNode
  tabId: string
}
export function TabItem({ children }: TabItemProps) {
  return children
}
